import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { reportWebVital } from './utils/telemetry';
import { preconnectApiOrigin } from './config/apiBase';
import { currentApp } from './config/origins';
import { NotFound, publicPageFor } from './publicRoutes';
import { INTRO_MS } from './components/marketing/heroIntro';

// Marketing CSS keys off this class for layout that only makes sense with
// JavaScript running (the product tour's scroll story). The prerendered pages'
// head script sets it too, before first paint.
document.documentElement.classList.add('js');

// Open the connection to the API origin while React is still mounting, so the
// first authenticated request does not also pay for DNS, TCP and TLS. Sourced
// from the RESOLVED base URL, never from the raw env var — production still has
// that set to a retired domain.
preconnectApiOrigin();

// ── Prerendered public pages ────────────────────────────────────────────────
// The public pages arrive as static HTML (scripts/prerender.js): #root already
// holds the page, marked data-prerendered, with the page's CSS in
// <style data-prerender>. React does not hydrate that markup. createRoot
// replaces it on the first commit, which avoids hydration mismatches caused by
// theme, sign-in state or screen size differing from the build-time render.
//
// To make that replacement invisible, the page's chunk is loaded BEFORE the
// first render (publicRoutes.lazyWithPreload), so the first commit is the page
// itself and not the Suspense spinner. The static copy stays on screen while
// the chunk loads.
const container = document.getElementById('root');
const prerendered = container.hasAttribute('data-prerendered');

function PrerenderHandoff({ children }) {
  useEffect(() => {
    // React's own styles are in place by now; the build-time copy would only
    // override a different theme (it is dark-mode CSS).
    document.querySelectorAll('style[data-prerender]').forEach((n) => n.remove());
    container.removeAttribute('data-prerendered');
    document.documentElement.removeAttribute('data-prerender-hide');
  }, []);
  return children;
}

async function start() {
  if (prerendered) {
    // The pages set these at runtime; the build-time copies would otherwise
    // outlive the page they describe after client-side navigation.
    document.head.querySelectorAll('link[rel="canonical"], meta[name="robots"]').forEach((n) => n.remove());
    if (currentApp() === 'main') {
      const Page = publicPageFor(window.location.pathname) || NotFound;
      // A failed preload is not fatal: the route's own Suspense boundary loads
      // it again, and a chunk error there reaches AppErrorBoundary.
      await Page.preload().catch(() => {});
    }
    // The landing hero plays its intro in CSS from the static first paint
    // (components/marketing/heroIntro.js). Replacing that markup restarts the
    // animation, so hand the elapsed time to the stylesheet as a negative
    // animation-delay: the replacements continue from where the static nodes
    // were. Waiting for the intro instead would delay the takeover — and with
    // it the hero image's final paint, which is this page's LCP.
    const html = document.documentElement;
    if (html.hasAttribute('data-intro')) {
      // HeroSection reads this rather than the attribute: on a slow load the
      // sequence can already be over by the time React commits, and the hero
      // must still not start a second intro of its own.
      window.__pfIntroMode = 'css';
      const elapsed = Math.max(0, performance.now() - (window.__pfIntroAt || 0));
      html.style.setProperty('--pf-intro-elapsed', `${Math.round(elapsed)}ms`);
      // Once the sequence is over the attribute goes, so a later client-side
      // visit to "/" plays the GSAP version instead of inheriting a finished
      // CSS animation whose fill state would override it.
      setTimeout(() => {
        html.removeAttribute('data-intro');
        html.style.removeProperty('--pf-intro-elapsed');
      }, Math.max(0, INTRO_MS - elapsed));
    }
  }

  const root = ReactDOM.createRoot(container);
  root.render(
    <React.StrictMode>
      <PrerenderHandoff>
        <App />
      </PrerenderHandoff>
    </React.StrictMode>
  );
}

start();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(reportWebVital);
